import React, { useEffect, useState } from "react";
import "./Forecast.css";
import { MapContainer, TileLayer, LayersControl, GeoJSON, Marker, Tooltip, LayerGroup, useMap } from "react-leaflet";
import L from "leaflet";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { MapHomeControl, MapInfoControl, MapLegendControl } from "../../../common/MapControls/MapControls";

const NLAKLO_CENTER = [-8.65, 125.75];
const NLAKLO_ZOOM = 10;
const NLAKLO_LAYER_NAMES = ["North Laklo River Network", "North Laklo Catchments", "North Laklo Sub-catchments"];

const MapZoomController = () => {
    const map = useMap();
    useEffect(() => {
        const onOverlayAdd = (e) => {
            if (NLAKLO_LAYER_NAMES.includes(e.name)) {
                map.setView(NLAKLO_CENTER, NLAKLO_ZOOM);
            }
        };
        map.on("overlayadd", onOverlayAdd);
        return () => map.off("overlayadd", onOverlayAdd);
    }, [map]);
    return null;
};

const ForecastCanvasJSChart = () => {
    const [boundaryData, setBoundaryData] = useState(null);
    const [DiliRivers, setDiliRivers] = useState(null);
    const [DilliCatchments, setDilliCatchments] = useState(null);
    const [NLakloRiver, setNLakloRiver] = useState(null);
    const [NLakloCatchments, setNLakloCatchments] = useState(null);
    const [NLakloSubCatchments] = useState(null);
    const [selCatchment, setSelCatchment] = useState(null);
    const [stations, setStations] = useState([]);
    const [selectedStation, setSelectedStation] = useState(null);
    const [activeTabStation, setActiveTabStation] = useState("graph");
    const [activeTabCatchment, setActiveTabCatchment] = useState("graph");
    const [stationChartOptions, setStationChartOptions] = useState(null);
    const [catchmentChartOptions, setCatchmentChartOptions] = useState(null);
    const [stationList, setStationList] = useState([]);
    const [catchmentList, setCatchmentList] = useState([]);
    const [loadingForecast, setLoadingForecast] = useState(false);
    const [loadingCatchment, setLoadingCatchment] = useState(false);
    const [selectedCatchment, setSelectedCatchment] = useState(null);
    const [catchmentRainfall, setCatchmentRainfall] = useState({});

    // const [selectedDate, setSelectedDate] = useState(() => {
    //     const todays = new Date();
    //     todays.setDate(todays.getDate());
    //     const yyyy = todays.getFullYear();
    //     const mm = String(todays.getMonth() + 1).padStart(2, '0');
    //     const dd = String(todays.getDate()).padStart(2, '0');
    //     return `${yyyy}-${mm}-${dd}`;
    // });
    const today = new Date().toISOString().split("T")[0];
    const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL;
    const defaultDays = 6;
    

    const stationIcon = L.icon({
        iconUrl: "/DATA/Flood/outlet.png", 
        iconSize: [15, 15],
        iconAnchor: [7, 7]
    });
    
    useEffect(() => {
        fetch("/DATA/OSMbased_adm1_boundary.json")
            .then((res) => res.json())
            .then(setBoundaryData)
            .catch(err => console.error("Boundary load error:", err));
    }, []);

    useEffect(() => {
        fetch("/DATA/Flood/dili_river.geojson")
            .then((res) => res.json())
            .then(setDiliRivers)
            .catch(err => console.error("Boundary load error:", err));
    }, []);

    useEffect(() => {
        fetch("/DATA/Flood/dili_catchments.geojson")
            .then((res) => res.json())
            .then(setDilliCatchments)
            .catch(err => console.error("Boundary load error:", err));
    }, []);

    useEffect(() => {
        fetch("/DATA/Flood/NLaklo_geojson_files/NorthLakloRiver.geojson")
            .then((res) => res.json())
            .then(setNLakloRiver)
            .catch(err => console.error("NLaklo River load error:", err));
    }, []);

    useEffect(() => {
        fetch("/DATA/Flood/NLaklo_geojson_files/NorthLaklo.geojson")
            .then((res) => res.json())
            .then(setNLakloCatchments)
            .catch(err => console.error("NLaklo Catchments load error:", err));
    }, []);

    useEffect(() => {
        fetch(`${backendUrl}/flood/hydrological-stations/`)
            .then((res) => res.json())
            .then((data) => {
                if (Array.isArray(data)) {
                    setStations(data);
                }
            })
            .catch(err => console.error("Stations load error:", err));
    }, [backendUrl]);

    // Auto-load default station on page load
    useEffect(() => {
        if (stations.length > 0 && !selectedStation) {
            const defaultStation = stations[0];
            setSelectedStation(defaultStation);
            fetchStationForecast(defaultStation);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stations]);

    // Auto-load default catchment on page load
    useEffect(() => {
        if (DilliCatchments && !selectedCatchment) {
            const firstFeature = DilliCatchments.features?.[0];
            if (firstFeature?.properties) {
                setSelectedCatchment(firstFeature.properties);
                fetchCatchmentRainfall(firstFeature.properties);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [DilliCatchments]);

    useEffect(() => {
        const fetchTodayRainfall = async () => {
            
            const url = `${backendUrl}/flood/catchment-precipitation-data/?dt=${today}&days=0`;
            try {
                const res = await fetch(url);
                const data = await res.json();

                if (Array.isArray(data)) {
                    const rainfallMap = {};
                    data.forEach(d => {
                        rainfallMap[d.catchment_id] = d.precipitation;
                    });
                    setCatchmentRainfall(rainfallMap);
                    // console.log("Today's rainfall map:", rainfallMap);
                }
            } catch (err) {
                console.error("Error fetching rainfall data:", err);
            }
        };
        fetchTodayRainfall();
    }, [backendUrl, today]);

    // No date changes for now; static 7-day window starting July 1
    const fetchCatchmentRainfall = (catchmentProps) => {
        if (!catchmentProps) return;
        setLoadingCatchment(true);

        // robust id detection: OBJECTID_1, OBJECTID, id, catchment_id, then name (for NLaklo S1–S14)
        const numericRaw = catchmentProps?.OBJECTID_1 ?? catchmentProps?.OBJECTID ?? catchmentProps?.id ?? catchmentProps?.catchment_id;
        const numericId = numericRaw != null ? Number(numericRaw) : null;
        const identifier = (numericId != null && !Number.isNaN(numericId))
            ? numericId
            : (catchmentProps?.name ?? null);

        if (identifier == null) {
            console.warn('Invalid catchment identifier for precipitation fetch:', catchmentProps);
            setLoadingCatchment(false);
            setCatchmentList([]);
            setCatchmentChartOptions(null);
            return;
        }

        const url = `${backendUrl}/flood/catchment-precipitation-data/?catchment_id=${encodeURIComponent(identifier)}&dt=${encodeURIComponent(today)}&days=${defaultDays}`;
        fetch(url)
            .then((res) => res.json())
            .then((data) => {
                let points = [];
                if (Array.isArray(data)) {
                    points = data
                        .filter((row) => row && row.dt && (row.precipitation != null))
                        .map((row) => {
                            const x = new Date(row.dt);
                            const y = Number(row.precipitation);
                            return { x, y };
                        })
                        .filter(pt => pt.x instanceof Date && !Number.isNaN(pt.x.getTime()) && Number.isFinite(pt.y));
                } else {
                    const datetimes = data.dt || [];
                    const rainfall = data.precipitation || [];
                    if (datetimes.length && rainfall.length) {
                        points = datetimes.map((dt, i) => {
                            const x = new Date(dt);
                            const y = Number(rainfall[i]);
                            return { x, y };
                        }).filter(pt => pt.x instanceof Date && !Number.isNaN(pt.x.getTime()) && Number.isFinite(pt.y));
                    }
                }

                if (!points.length) {
                    // keep list for "List" tab, but clear chart options
                    setCatchmentList([]);
                    setCatchmentChartOptions(null);
                    return;
                }

                setCatchmentList(points);

                const hcData = points.map(p => [p.x.getTime(), p.y]);
                const options = {
                    chart: {
                        type: "column",
                        animation: true,
                        backgroundColor: "#ffffff",
                        spacing: [10, 10, 20, 10],
                        style: { fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" },
                    },
                    title: { text: null },
                    xAxis: {
                        type: "datetime",
                        dateTimeLabelFormats: { day: "%e %b", week: "%e %b", month: "%b '%y" },
                        labels: {
                            enabled: true,
                            style: { fontSize: "11px", color: "#475569", fontWeight: "500" },
                            rotation: 0,
                            overflow: "allow",
                        },
                        lineColor: "#e2e8f0",
                        tickColor: "#e2e8f0",
                        crosshair: { width: 1, color: "rgba(37,99,235,0.3)", dashStyle: "Dash" },
                    },
                    yAxis: {
                        title: { text: "Rainfall (mm)", style: { fontSize: "11px", color: "#64748b", fontWeight: "600" } },
                        labels: { style: { fontSize: "11px", color: "#475569" } },
                        min: 0,
                        gridLineColor: "#f1f5f9",
                        gridLineDashStyle: "Dot",
                    },
                    tooltip: {
                        shared: true,
                        backgroundColor: "rgba(15,23,42,0.92)",
                        borderColor: "transparent",
                        borderRadius: 10,
                        shadow: false,
                        style: { color: "#f8fafc", fontSize: "12px", fontWeight: "500" },
                        headerFormat: '<span style="font-size:11px;color:#94a3b8;font-weight:600;">{point.key}</span><br/>',
                        pointFormat: '<span style="color:{series.color};">●</span> <b>{series.name}:</b> <b>{point.y:.2f}</b> mm<br/>',
                    },
                    legend: { enabled: false },
                    plotOptions: {
                        column: {
                            borderRadius: 4,
                            borderWidth: 0,
                            groupPadding: 0.15,
                            pointPadding: 0.05,
                        },
                    },
                    series: [{ name: "Rainfall (mm)", data: hcData, color: "#2563eb" }],
                    credits: { enabled: false },
                };
                setCatchmentChartOptions(options);
            })
            .catch((err) => {
                console.error("Catchment rainfall load error:", err, url);
                setCatchmentList([]);
                setCatchmentChartOptions(null);
            })
            .finally(() => setLoadingCatchment(false));
    };

    const fetchStationForecast = (station) => {
        if (!station || !station.id) return;
        setLoadingForecast(true);
        // Use fixed start date (July 1) and 7 days
        const startDate = today;
        const url = `${backendUrl}/flood/hydrological-stations-discharge-data/?station_id=${encodeURIComponent(station.id)}&dt=${encodeURIComponent(startDate)}&days=${defaultDays}`;
        fetch(url)
            .then((res) => res.json())
            .then((data) => {
                let points = [];
                if (Array.isArray(data)) {
                    // Expected array of objects: [{ dt: 'YYYY-MM-DD', discharge: number }]
                    points = data
                        .filter((row) => row && row.dt && (row.discharge != null || row.flow_median != null))
                        .map((row) => ({ x: new Date(row.dt), y: Number(row.discharge ?? row.flow_median) }));
                } else {
                    const datetimes = data.dt || data.datetimes || [];
                    const discharge = data.discharge || data.flow_median || [];
                    if (datetimes.length && discharge.length) {
                        points = datetimes.map((dt, i) => ({ x: new Date(dt), y: discharge[i] }));
                    }
                }
                if (!points.length) {
                    setStationList([]);
                    setStationChartOptions(null);
                    return;
                }
                setStationList(points);
                const hcData = points.map(p => [p.x.getTime(), p.y]);
                const options = {
                    chart: {
                        type: "spline",
                        animation: true,
                        backgroundColor: "#ffffff",
                        spacing: [10, 10, 20, 10],
                        style: { fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" },
                    },
                    title: { text: null },
                    xAxis: {
                        type: "datetime",
                        dateTimeLabelFormats: { day: "%e %b", week: "%e %b", month: "%b '%y" },
                        labels: {
                            enabled: true,
                            style: { fontSize: "11px", color: "#475569", fontWeight: "500" },
                            rotation: 0,
                            overflow: "allow",
                        },
                        lineColor: "#e2e8f0",
                        tickColor: "#e2e8f0",
                        crosshair: { width: 1, color: "rgba(37,99,235,0.3)", dashStyle: "Dash" },
                    },
                    yAxis: {
                        title: { text: "Discharge (m³/s)", style: { fontSize: "11px", color: "#64748b", fontWeight: "600" } },
                        labels: { style: { fontSize: "11px", color: "#475569" } },
                        min: 0,
                        gridLineColor: "#f1f5f9",
                        gridLineDashStyle: "Dot",
                    },
                    tooltip: {
                        shared: true,
                        backgroundColor: "rgba(15,23,42,0.92)",
                        borderColor: "transparent",
                        borderRadius: 10,
                        shadow: false,
                        style: { color: "#f8fafc", fontSize: "12px", fontWeight: "500" },
                        headerFormat: '<span style="font-size:11px;color:#94a3b8;font-weight:600;">{point.key}</span><br/>',
                        pointFormat: '<span style="color:{series.color};">●</span> <b>{series.name}:</b> <b>{point.y:.2f}</b> m³/s<br/>',
                    },
                    legend: { enabled: false },
                    plotOptions: {
                        spline: {
                            lineWidth: 2.5,
                            marker: { radius: 3, symbol: "circle", states: { hover: { radiusPlus: 3 } } },
                        },
                    },
                    series: [{ name: "Discharge (m³/s)", data: hcData, color: "#2563eb", lineWidth: 2.5 }],
                    credits: { enabled: false },
                };
                setStationChartOptions(options);
            })
            .catch((err) => {
                console.error("Station forecast load error:", err);
                setStationList([]);
                setStationChartOptions(null);
            })
            .finally(() => setLoadingForecast(false));
    };
    // Define a function to compute color based on rainfall
    const getCatchmentColor = (val) => {
        if (val >= 60) return "rgb(230,45,58)";       // Extreme
        if (val >= 25) return "rgb(243,155,58)";      // Heavy
        if (val >= 15) return "rgb(243,235,18)";      // Moderate
        if (val >= 0.01) return "rgb(148,203,85)";    // Light
        return "rgb(222,222,223)";                    // No rainfall
    };
    const catchmentStyle = (feature) => {
        const id = feature.properties?.OBJECTID_1 ?? feature.properties?.OBJECTID ?? feature.properties?.id ?? feature.properties?.name;
        const rainfall = catchmentRainfall[id] || 0;
        const color = getCatchmentColor(rainfall);
        return {
            color: "#555",
            weight: 1,
            fillColor: color,
            fillOpacity: 0.7
        };
    };
    const CatchmentFeature = (feature, layer) => {
        const id = feature.properties?.OBJECTID_1 ?? feature.properties?.OBJECTID ?? feature.properties?.id;
        const rainfall = catchmentRainfall[id] ?? 0;
        layer.on({
            mouseover: () => {
                setSelCatchment(`
                    <div>
                        <strong>Catchment</strong>
                        <p>Name: ${feature.properties.Catchment_ ?? feature.properties.name ?? "N/A"}<br/> Rainfall: ${Number(rainfall).toFixed(1)} mm</p>
                    </div>
                `);
                try { layer.setStyle({ weight: 3, fillOpacity: 0.5 }); } catch (e) {}
                if (layer.bringToFront) { try { layer.bringToFront(); } catch (e) {} }
            },
            mouseout: () => {
                setSelCatchment(null);
                try { layer.setStyle({ weight: 1, fillOpacity: 0.1 }); } catch (e) {}
            },
            click: () => {
                setSelectedCatchment(feature.properties || {});
                setActiveTabCatchment("graph");
                fetchCatchmentRainfall(feature.properties || {});
            }
        });
    };

    const toggleBtnStyle = (isActive) => ({
        padding: "5px 16px",
        fontSize: "11px",
        fontWeight: isActive ? 700 : 500,
        letterSpacing: "0.3px",
        borderRadius: "6px",
        border: "none",
        backgroundColor: isActive ? "#2563eb" : "transparent",
        color: isActive ? "#fff" : "#64748b",
        cursor: "pointer",
        boxShadow: isActive ? "0 2px 6px rgba(37,99,235,0.25)" : "none",
        transition: "all 0.2s ease",
        outline: "none",
    });

    const panelCardStyle = {
        background: "#ffffff",
        borderRadius: 12,
        boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
        border: "1px solid #f1f5f9",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
    };

    const thStyle = {
        textAlign: "left",
        padding: "10px 12px",
        borderBottom: "2px solid #e2e8f0",
        fontWeight: 600,
        fontSize: "11px",
        color: "#64748b",
        letterSpacing: "0.3px",
        textTransform: "uppercase",
        background: "#f8fafc",
    };

    const tdStyle = (align = "left") => ({
        textAlign: align,
        padding: "10px 12px",
        fontSize: "12px",
        color: "#1e293b",
        fontWeight: 500,
    });

    const emptyState = (icon, text) => (
        <div style={{
            flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
            justifyContent: "center", background: "#f8fafc", borderRadius: 8,
            border: "2px dashed #e2e8f0", gap: 10, height: "100%",
        }}>
            <div style={{
                width: 48, height: 48, borderRadius: "50%",
                background: "linear-gradient(135deg, #ebf2ff, #dbeafe)",
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22,
            }}>{icon}</div>
            <p style={{ color: "#94a3b8", fontSize: 13, margin: 0, fontWeight: 500 }}>{text}</p>
        </div>
    );

    return (
        <div style={{ display: "flex", gap: 12, height: "calc(100vh - 120px)" }}>
            {/* LEFT COLUMN — Map */}
            <div style={{ flex: "1 1 60%", minWidth: 0, position: "relative", borderRadius: 12, overflow: "hidden" }}>
                <MapContainer
                    center={[-8.6450, 125.5877]}
                    zoom={10}
                    maxZoom={22}
                    zoomControl={false}
                    attributionControl={false}
                    style={{ height: "100%", width: "100%", borderRadius: 12 }}
                >
                  <MapHomeControl />
                  <MapInfoControl text="Hydrological forecast for river stations. Click a station to view forecast details." />
                    <LayersControl position="topright">
                        <LayersControl.Overlay name="Timor-Leste">
                            {boundaryData && <GeoJSON data={boundaryData} interactive={false} style={{ color: "#ff0000", weight: 1, fillOpacity: 0 }} />}
                        </LayersControl.Overlay>
                        <LayersControl.Overlay name="Dili River Network" checked>
                            {DiliRivers && <GeoJSON data={DiliRivers} interactive={false} style={{ color: "#0099FF", weight: 2, fillOpacity: 0 }} />}
                        </LayersControl.Overlay>
                        <LayersControl.Overlay name="Stations" checked>
                            <LayerGroup>
                                {stations && stations.map((s) => (
                                    <Marker
                                        key={s.id}
                                        position={[s.latitude, s.longitude]}
                                        icon={stationIcon}
                                        eventHandlers={{
                                            click: () => {
                                                setSelectedStation(s);
                                                setActiveTabStation("graph");
                                                fetchStationForecast(s);
                                            }
                                        }}
                                    >
                                        <Tooltip direction="right" offset={[8, 0]} opacity={1} permanent={false}>
                                            {s.station_name}
                                        </Tooltip>
                                    </Marker>
                                ))}
                            </LayerGroup>
                        </LayersControl.Overlay>
                        <LayersControl.Overlay name="Dili Catchment" checked>
                            {DilliCatchments && (
                                <GeoJSON
                                    key={Object.keys(catchmentRainfall).length}
                                    data={DilliCatchments}
                                    style={catchmentStyle}
                                    interactive={true}
                                    onEachFeature={CatchmentFeature}
                                />
                            )}
                        </LayersControl.Overlay>
                        <LayersControl.Overlay name="North Laklo River Network" checked>
                            {NLakloRiver && (
                                <GeoJSON
                                    data={NLakloRiver}
                                    interactive={false}
                                    style={{ color: "#0077CC", weight: 2, fillOpacity: 0 }}
                                />
                            )}
                        </LayersControl.Overlay>
                        <LayersControl.Overlay name="North Laklo Catchments" checked>
                            {NLakloCatchments && (
                                <GeoJSON
                                    key={Object.keys(catchmentRainfall).length + "_nlaklo"}
                                    data={NLakloCatchments}
                                    style={catchmentStyle}
                                    interactive={true}
                                    onEachFeature={CatchmentFeature}
                                />
                            )}
                        </LayersControl.Overlay>
                        <LayersControl.Overlay name="North Laklo Sub-catchments">
                            {NLakloSubCatchments && (
                                <GeoJSON
                                    key={Object.keys(catchmentRainfall).length + "_nlaklo_sub"}
                                    data={NLakloSubCatchments}
                                    style={catchmentStyle}
                                    interactive={true}
                                    onEachFeature={CatchmentFeature}
                                />
                            )}
                        </LayersControl.Overlay>
                    </LayersControl>
                    <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    />
                    <MapZoomController />

                    {/* Hover info box */}
                    {selCatchment && (
                        <div
                            className="hover-box-hyd-forecast"
                            style={{ position: "absolute",
                            top: 10,
                            left: 50,
                            zIndex: 1000,
                            background: "rgba(255, 255, 255, 0.92)",
                            backdropFilter: "blur(12px)",
                            WebkitBackdropFilter: "blur(12px)",
                            borderRadius: 10,
                            padding: "4px 6px 0px 7px",
                            border: "1px solid #e2e8f0",
                            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.08)",
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            maxWidth: 260,
                            pointerEvents: "none",
                            fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" }}
                            dangerouslySetInnerHTML={{ __html: selCatchment }}
                        />
                    )}

                    {/* Legend */}
                    <MapLegendControl
                        width="220px"
                        sections={[
                            {
                                title: "Network & Catchments",
                                shape: "square",
                                items: [
                                    { label: "Dili River Network", icon: <span style={{ display: "inline-block", width: "18px", height: "3px", borderRadius: "2px", backgroundColor: "#0099FF" }} /> },
                                    { label: "Timor-Leste Boundary", color: "#ff0000" },
                                    { label: "Dili Catchment", color: "#125ca6" },
                                    { label: "Station", icon: <img src="/DATA/Flood/outlet.png" alt="Station" style={{ width: "18px", height: "18px" }} /> },
                                    { label: "N. Laklo River Network", icon: <span style={{ display: "inline-block", width: "18px", height: "3px", borderRadius: "2px", backgroundColor: "#0077CC" }} /> },
                                    { label: "N. Laklo Catchments", color: "#6aafe6" },
                                ],
                            },
                            {
                                title: "Rainfall (mm)",
                                shape: "square",
                                items: [
                                    { label: "0", color: "rgb(222,222,223)" },
                                    { label: "0.01-14", color: "rgb(148,203,85)" },
                                    { label: "15-24", color: "rgb(243,235,18)" },
                                    { label: "25-59", color: "rgb(243,155,58)" },
                                    { label: "60+", color: "rgb(230,45,58)" },
                                ],
                            },
                        ]}
                    />
                </MapContainer>
            </div>

            {/* RIGHT COLUMN — Charts */}
            <div style={{ flex: "1 1 40%", minWidth: 0, display: "flex", flexDirection: "column", gap: 12 }}>

                {/* Row 1 — Station Discharge */}
                <div style={{ ...panelCardStyle, flex: 1, minHeight: 0 }}>
                    <div style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        padding: "12px 18px", borderBottom: "1px solid #f1f5f9",
                    }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div style={{
                                width: 8, height: 8, borderRadius: "50%", background: "#2563eb",
                                boxShadow: "0 0 8px rgba(37,99,235,0.4)", flexShrink: 0,
                            }} />
                            <div>
                                <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", letterSpacing: "0.2px" }}>
                                    {selectedStation?.station_name || "Station"}
                                </div>
                                <div style={{ fontSize: 11, color: "#64748b", fontWeight: 500, marginTop: 1 }}>
                                    7 Days Discharge Forecast
                                </div>
                            </div>
                        </div>
                        <div style={{ display: "flex", backgroundColor: "#f1f5f9", borderRadius: 8, padding: 3, gap: 2 }}>
                            <button style={toggleBtnStyle(activeTabStation === "graph")} onClick={() => setActiveTabStation("graph")}>Chart</button>
                            <button style={toggleBtnStyle(activeTabStation === "list")} onClick={() => setActiveTabStation("list")}>List</button>
                        </div>
                    </div>
                    <div style={{ flex: 1, minHeight: 0, padding: activeTabStation === "graph" ? "8px 4px 12px 4px" : 0, overflow: "hidden" }}>
                        {loadingForecast ? (
                            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                                <img src="/loader.webp" alt="Loading..." style={{ height: 36 }} />
                            </div>
                        ) : activeTabStation === "graph" ? (
                            stationChartOptions ? (
                                <div style={{ width: "100%", height: "100%" }}>
                                    <HighchartsReact
                                        highcharts={Highcharts}
                                        options={stationChartOptions}
                                        containerProps={{ style: { width: "100%", height: "100%" } }}
                                    />
                                </div>
                            ) : emptyState("Click a station on the map to view discharge data")
                        ) : (
                            <div style={{ height: "100%", overflowY: "auto" }}>
                                {stationList.length > 0 ? (
                                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                        <thead>
                                            <tr>
                                                <th style={{ ...thStyle, textTransform: "none" }}>Date</th>
                                                <th style={{ ...thStyle, textAlign: "right", textTransform: "none" }}>Discharge (m³/s)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {stationList.map((pt, idx) => (
                                                <tr key={idx} style={{ borderBottom: "1px solid #f1f5f9", transition: "background 0.15s" }}
                                                    onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
                                                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                                                >
                                                    <td style={tdStyle("left")}>{pt.x instanceof Date ? pt.x.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' }) : String(pt.x)}</td>
                                                    <td style={tdStyle("right")}>{typeof pt.y === 'number' ? pt.y.toFixed(2) : pt.y}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : emptyState("No discharge data available")}
                            </div>
                        )}
                    </div>
                </div>

                {/* Row 2 — Catchment Rainfall */}
                <div style={{ ...panelCardStyle, flex: 1, minHeight: 0 }}>
                    <div style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        padding: "12px 18px", borderBottom: "1px solid #f1f5f9",
                    }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div style={{
                                width: 8, height: 8, borderRadius: "50%", background: "#16a34a",
                                boxShadow: "0 0 8px rgba(22,163,74,0.4)", flexShrink: 0,
                            }} />
                            <div>
                                <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", letterSpacing: "0.2px" }}>
                                    {selectedCatchment?.Catchment_ || selectedCatchment?.name || "Catchment"}
                                </div>
                                <div style={{ fontSize: 11, color: "#64748b", fontWeight: 500, marginTop: 1 }}>
                                    7 Days Rainfall
                                </div>
                            </div>
                        </div>
                        <div style={{ display: "flex", backgroundColor: "#f1f5f9", borderRadius: 8, padding: 3, gap: 2 }}>
                            <button style={toggleBtnStyle(activeTabCatchment === "graph")} onClick={() => setActiveTabCatchment("graph")}>Chart</button>
                            <button style={toggleBtnStyle(activeTabCatchment === "list")} onClick={() => setActiveTabCatchment("list")}>List</button>
                        </div>
                    </div>
                    <div style={{ flex: 1, minHeight: 0, padding: activeTabCatchment === "graph" ? "8px 4px 12px 4px" : 0, overflow: "hidden" }}>
                        {loadingCatchment ? (
                            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                                <img src="/loader.webp" alt="Loading..." style={{ height: 36 }} />
                            </div>
                        ) : activeTabCatchment === "graph" ? (
                            catchmentChartOptions ? (
                                <div style={{ width: "100%", height: "100%" }}>
                                    <HighchartsReact
                                        highcharts={Highcharts}
                                        options={catchmentChartOptions}
                                        containerProps={{ style: { width: "100%", height: "100%" } }}
                                    />
                                </div>
                            ) : emptyState("Click a catchment on the map to view rainfall data")
                        ) : (
                            <div style={{ height: "100%", overflowY: "auto" }}>
                                {catchmentList.length > 0 ? (
                                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                        <thead>
                                            <tr>
                                                <th style={{ ...thStyle, textTransform: "none" }}>Date</th>
                                                <th style={{ ...thStyle, textAlign: "right", textTransform: "none" }}>Rainfall (mm)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {catchmentList.map((pt, idx) => (
                                                <tr key={idx} style={{ borderBottom: "1px solid #f1f5f9", transition: "background 0.15s" }}
                                                    onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
                                                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                                                >
                                                    <td style={tdStyle("left")}>{pt.x instanceof Date ? pt.x.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' }) : String(pt.x)}</td>
                                                    <td style={tdStyle("right")}>{typeof pt.y === 'number' ? pt.y.toFixed(2) : pt.y}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : emptyState("📋", "No rainfall data available")}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
export default ForecastCanvasJSChart;